import React, { useEffect, useState } from 'react';
import { useAppStore } from '@alveon/shared/store';
import { initTelegramWebApp } from './utils/telegram';
import { apiClient, startPowerSync } from './platform/init';
import { logger } from './utils/logger';
import { initPlatformStyles } from './utils/platform';
import { BottomNav } from './components/BottomNav';
import { Icon } from './components/Icon';
import { HomeScreen } from './screens/HomeScreen';
import { OperationsScreen } from './screens/OperationsScreen';
import { GoalsWithTabsScreen } from './screens/GoalsWithTabsScreen';
import { AIChatScreen } from './screens/AIChatScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { SubscriptionScreen } from './screens/SubscriptionScreen';
import { PlansScreen } from './screens/PlansScreen';
import { NotificationsScreen } from './screens/NotificationsScreen';
import { LoginScreen } from './screens/LoginScreen';
import { SecurityScreen } from './screens/SecurityScreen';
import { PrivacyPolicyScreen } from './screens/PrivacyPolicyScreen';
import { TermsOfServiceScreen } from './screens/TermsOfServiceScreen';
import { WelcomeScreen } from './screens/WelcomeScreen';
import { RequestPasswordResetScreen } from './screens/RequestPasswordResetScreen';
import { ResetPasswordScreen } from './screens/ResetPasswordScreen';
import { AccountsScreen } from './screens/AccountsScreen';
import { NotificationPoller } from './components/NotificationPoller';
import { NotificationPopup } from './components/NotificationPopup';
import { useTheme } from './hooks/useTheme';
import { useTelegramPaymentWatcher } from './hooks/useTelegramPaymentWatcher';
import { trackPageView } from './services/analytics';
import { PromoCodeModal } from './components/PromoCodeModal';
import { PlatformDebugger } from './components/PlatformDebugger';
// PowerSync обрабатывает синхронизацию автоматически
import './i18n/config'; // Инициализация i18next
import './App.css';

function App() {
  const { currentPage, setCurrentPage, setLoading, setError, setSettings, settings } = useAppStore();
  const [initialized, setInitialized] = useState(false);
  const [showDevBanner, setShowDevBanner] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<any>(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [urlKey, setUrlKey] = useState(0); // Для принудительного перерендера при изменении URL
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [showPromoModal, setShowPromoModal] = useState(false);
  
  // Инициализируем тему
  useTheme();
  
  // Отслеживаем статус оплаты через Telegram (автоматический разлогин после связки)
  useTelegramPaymentWatcher();

  // Прокрутка наверх при смене страницы и отслеживание в GA
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Отслеживаем просмотр страницы в Google Analytics
    const pageTitle = {
      'home': 'Главная',
      'operations': 'Операции',
      'goals': 'Цели',
      'ai-chat': 'AI Чат',
      'settings': 'Настройки',
      'subscription': 'Подписка',
      'plans': 'Планы',
      'notifications': 'Уведомления',
      'security': 'Безопасность',
      'privacy': 'Политика конфиденциальности',
      'terms': 'Условия использования',
      'accounts': 'Счета',
      'request-password-reset': 'Восстановление пароля',
      'reset-password': 'Сброс пароля',
    }[currentPage] || currentPage;
    
    trackPageView(`/${currentPage}`, pageTitle);
  }, [currentPage]);

  // Запуск автосинхронизации и PowerSync после инициализации и авторизации
  useEffect(() => {
    if (initialized && !needsLogin) {
      logger.info('Starting auto-sync...');
      
      // 🔄 Запускаем PowerSync
      startPowerSync().then(() => {
        logger.info('[PowerSync] Started successfully');
      }).catch((err) => {
        logger.warn('[PowerSync] Start failed (non-critical):', err);
      });
      
      // PowerSync обрабатывает синхронизацию автоматически
      
      return () => {
        // Cleanup если нужен
      };
    }
  }, [initialized, needsLogin]);

  useEffect(() => {
    // Инициализируем стили для конкретной платформы
    initPlatformStyles();
    
    // Очищаем флаг обработки истечения сессии при загрузке
    sessionStorage.removeItem('session_expired_handled');
    
    // ПЕРВЫМ ДЕЛОМ проверяем параметр login=true в URL (обрабатываем всегда, независимо от всего)
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    
    // Проверяем промокод в URL (показываем только один раз)
    const promoParam = params.get('promo');
    if (promoParam && !sessionStorage.getItem(`promo_shown_${promoParam}`)) {
      logger.info(`Promo code detected in URL: ${promoParam}`);
      setPromoCode(promoParam);
      setShowPromoModal(true);
      sessionStorage.setItem(`promo_shown_${promoParam}`, 'true');
    }
    
    if (params.get('login') === 'true') {
      // Если есть параметр login=true - принудительно показываем LoginScreen
      console.log('🔵 [App] Login parameter detected in URL, forcing LoginScreen');
      console.log('🔵 [App] Setting needsLogin=true, initialized=true');
      logger.info('Login parameter detected, forcing LoginScreen');
      // Очищаем параметр из URL СРАЗУ
      window.history.replaceState({}, '', path);
      // Устанавливаем needsLogin и initialized БЕЗ вызова других функций
      setNeedsLogin(true);
      setInitialized(true);
      // НЕ вызываем initializeApp() и другие функции
      console.log('🔵 [App] Early return, skipping initializeApp()');
      return;
    }
    
    // Проверяем, если это публичные страницы - показываем их без инициализации
    if (path === '/welcome' || path === '/privacy' || path === '/terms') {
      setInitialized(true);
      return;
    }
    
    // Если корневой путь и нет токена - проверяем параметры
    // НО только если needsLogin еще не установлен (чтобы не перезаписать его)
    if (path === '/' && !localStorage.getItem('auth_token') && !window.Telegram?.WebApp?.initData) {
      // Проверяем OAuth callback параметры в URL
      handleOAuthCallback();
      // Если нет токена после обработки callback
      if (!params.get('token')) {
        // НЕ устанавливаем needsLogin=false если он уже был установлен в true
        // Просто показываем WelcomeScreen через isUnauthenticated
        setInitialized(true);
        return;
      }
    }
    
    // Проверяем OAuth callback параметры в URL
    handleOAuthCallback();
    // Затем инициализируем приложение
    initializeApp();
  }, []);

  // Слушаем изменения URL для обработки параметра login=true
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('login') === 'true') {
        console.log('🔵 [App] PopState: login=true detected, setting needsLogin=true');
        setNeedsLogin(true);
      }
      // НЕ сбрасываем needsLogin если параметра нет - может быть установлен программно
      // Принудительно обновляем компонент
      setUrlKey(prev => prev + 1);
    };
    
    window.addEventListener('popstate', handlePopState);
    // Также слушаем собственное событие для программного изменения URL
    window.addEventListener('pushstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pushstate', handlePopState);
    };
  }, []);

  const handleOAuthCallback = () => {
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    
    // Успешный вход через Google (НЕ на странице сброса пароля!)
    const token = params.get('token');
    if (token && path !== '/reset-password' && !path.includes('reset-password')) {
      logger.info('OAuth login successful, saving token');
      localStorage.setItem('auth_token', token);
      apiClient.setToken(token);
      
      const userId = params.get('user_id');
      if (userId) {
        localStorage.setItem('user_id', userId);
      }
      
      // Очищаем URL
      window.history.replaceState({}, '', '/');
      return;
    }
    
    // Успешная привязка OAuth провайдера
    const oauthSuccess = params.get('oauth_success');
    if (oauthSuccess === 'true') {
      const provider = params.get('linked');
      logger.info(`${provider} successfully linked`);
      window.history.replaceState({}, '', '/');
      return;
    }
    
    // Ошибка OAuth
    const oauthError = params.get('oauth_error');
    if (oauthError) {
      logger.error(`OAuth error: ${oauthError}`);
      window.history.replaceState({}, '', '/');
      return;
    }
  };

  const initializeApp = async () => {
    try {
      setLoading(true);
      
      // Проверяем URL для роутинга
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);
      
      // Если есть параметр login=true - принудительно показываем LoginScreen (даже для авторизованных)
      if (params.get('login') === 'true') {
        logger.info('Login parameter detected in initializeApp, forcing LoginScreen');
        setNeedsLogin(true);
        setInitialized(true);
        setLoading(false);
        return;
      }
      
      // Специальные пути, которые не требуют авторизации
      if (path === '/request-password-reset' || path.includes('request-password-reset')) {
        setCurrentPage('request-password-reset');
        setInitialized(true);
        setNeedsLogin(false);
        setLoading(false);
        return;
      }
      
      if (path === '/reset-password' || path.includes('reset-password') || params.get('token')) {
        setCurrentPage('reset-password');
        setInitialized(true);
        setNeedsLogin(false);
        setLoading(false);
        return;
      }
      
      // Инициализация Telegram WebApp
      const tg = initTelegramWebApp();
      logger.debug('Telegram WebApp initialized:', tg?.version);
      
      // Проверяем наличие Telegram initData
      const hasInitData = !!window.Telegram?.WebApp?.initData;
      
      // Проверяем наличие сохраненного токена
      const savedToken = localStorage.getItem('auth_token');
      
      // Если есть сохраненный токен, проверяем его валидность
      if (savedToken && !hasInitData) {
        logger.debug('Using saved token for authentication');
        apiClient.setToken(savedToken);
        
        // Проверяем валидность токена через запрос подписки
        try {
          const subscriptionData = await apiClient.getSubscription();
          const status = subscriptionData.subscription.status;
          setSettings({
            ...settings,
            subscription_status: status,
            subscription_expires_at: subscriptionData.subscription.subscription_end || undefined,
          });
          logger.info('Subscription loaded:', status);
          // Токен валидный - продолжаем работу
          setNeedsLogin(false);
        } catch (subError: any) {
          logger.error('Failed to load subscription:', subError);
          // Если получили 401 - токен невалидный, очищаем и показываем логин
          if (subError?.message?.includes('Token is missing') || 
              subError?.message?.includes('Unauthorized') ||
              subError?.message?.includes('Session expired')) {
            logger.warn('Token is invalid, clearing and showing login');
            localStorage.removeItem('auth_token');
            apiClient.setToken('');
            setNeedsLogin(true);
          } else {
            // Другие ошибки - возможно временные, но все равно показываем логин для безопасности
            setNeedsLogin(true);
          }
        }
        
        setInitialized(true);
        return;
      }
      
      // Проверяем флаг удаления аккаунта
      const accountDeleted = localStorage.getItem('account_deleted');
      if (accountDeleted === 'true' && hasInitData) {
        logger.warn('Account was deleted, showing login screen');
        setNeedsLogin(true);
        setInitialized(true);
        return;
      }
      
      // Если нет ни initData, ни токена - показываем логин
      if (!hasInitData && !savedToken) {
        logger.debug('No auth data, showing login screen');
        setNeedsLogin(true);
        setInitialized(true);
        return;
      }
      
      // Получение токена через Telegram initData или dev auth
      try {
        const authResponse = await apiClient.validateAndGetToken();
        logger.info('Auth successful:', authResponse.status === 'ok');
        setNeedsLogin(false);
        
        // Загружаем данные подписки для работы премиум-функций
        try {
          const subscriptionData = await apiClient.getSubscription();
          const status = subscriptionData.subscription.status;
          setSettings({
            ...settings,
            subscription_status: status,
            subscription_expires_at: subscriptionData.subscription.subscription_end || undefined,
          });
          logger.info('Subscription loaded:', status);
        } catch (subError) {
          logger.error('Failed to load subscription:', subError);
        }
      } catch (authError: any) {
        logger.error('Auth error:', authError);
        // Если не удалось авторизоваться - показываем логин
        // Особенно если нет initData (браузер, не Telegram)
        if (!hasInitData || authError?.message?.includes('initData is missing')) {
          setNeedsLogin(true);
          // Очищаем токен, если он был невалидный
          if (savedToken) {
            localStorage.removeItem('auth_token');
            apiClient.setToken('');
          }
        } else if (!savedToken) {
          // Если есть initData, но авторизация не удалась и нет токена
          setNeedsLogin(true);
        }
      }
      
      setInitialized(true);
    } catch (error) {
      logger.error('App initialization error:', error);
      setError(error instanceof Error ? error.message : 'Ошибка инициализации');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    logger.info('Login successful, updating app state');
    setNeedsLogin(false);
    // Не перезагружаем страницу, просто обновляем состояние
    // Автосинхронизация запустится автоматически через useEffect
  };

  if (!initialized) {
    return (
      <div className="app-loading">
        <div className="app-loading__spinner" />
        <p className="app-loading__text">Загрузка...</p>
      </div>
    );
  }

  // ПЕРВЫМ ДЕЛОМ проверяем needsLogin - если true, показываем LoginScreen принудительно
  console.log('🔵 [App] Render check - needsLogin:', needsLogin, 'initialized:', initialized, 'currentPage:', currentPage);
  if (needsLogin) {
    console.log('🔵 [App] ✅ needsLogin=true, RETURNING LoginScreen. Current state:', { needsLogin, initialized, currentPage });
    logger.info('needsLogin=true, showing LoginScreen');
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }
  
  // Проверяем публичные страницы (доступны без авторизации)
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  
  // Дополнительная проверка параметра login=true в URL (на случай если needsLogin не установился)
  if (params.get('login') === 'true') {
    console.log('🔵 [App] Login parameter STILL in URL, showing LoginScreen directly');
    logger.info('Login parameter STILL in URL, showing LoginScreen directly');
    window.history.replaceState({}, '', path);
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }
  
  const hasToken = localStorage.getItem('auth_token');
  const hasTelegramInit = !!window.Telegram?.WebApp?.initData;
  
  // Если нет токена и нет Telegram initData - всегда показываем экран авторизации
  const isUnauthenticated = !hasToken && !hasTelegramInit;
  
  // Если есть параметр login=true в URL - ВСЕГДА показываем LoginScreen (принудительно)
  // Это позволяет войти через веб-интерфейс даже если есть Telegram initData
  if (params.get('login') === 'true') {
    console.log('🔵 [App] Login parameter in URL, showing LoginScreen');
    logger.info('Login parameter in URL, showing LoginScreen');
    // Очищаем параметр из URL
    window.history.replaceState({}, '', path);
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }
  
  // Показываем WelcomeScreen на корневом пути для неавторизованных пользователей
  if (path === '/' && isUnauthenticated) {
    return <WelcomeScreen />;
  }
  
  if (path === '/welcome') {
    return <WelcomeScreen />;
  }
  if (path === '/privacy') {
    return <PrivacyPolicyScreen />;
  }
  if (path === '/terms') {
    return <TermsOfServiceScreen />;
  }

  // Страницы восстановления пароля (доступны без авторизации)
  if (currentPage === 'request-password-reset') {
    return <RequestPasswordResetScreen />;
  }
  if (currentPage === 'reset-password') {
    return <ResetPasswordScreen />;
  }

  // Показываем экран логина, если пользователь неавторизован и не на корневом пути
  if (isUnauthenticated && path !== '/') {
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }
  
  // Если пользователь неавторизован и дошли до этого места - показываем WelcomeScreen
  if (isUnauthenticated) {
    return <WelcomeScreen />;
  }

  // ВАЖНО: Проверяем needsLogin ПОСЛЕДНИМ перед рендером основного приложения
  // Это гарантирует, что LoginScreen показывается даже если currentPage установлен
  if (needsLogin) {
    console.log('🔵 [App] needsLogin=true at final check, showing LoginScreen');
    logger.info('needsLogin=true at final check, showing LoginScreen');
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="app">
      {/* Фоновый поллинг уведомлений */}
      <NotificationPoller 
        enabled={true} 
        interval={60000}
        onNewNotification={(notification) => {
          setCurrentNotification(notification);
        }}
      />
      
      {/* Попап уведомлений через Polling */}
      <NotificationPopup 
        notification={currentNotification}
        duration={5000}
        onClose={() => setCurrentNotification(null)}
      />
      
      <div className="app__content">
        {currentPage === 'home' && !needsLogin && <HomeScreen />}
        {currentPage === 'operations' && <OperationsScreen />}
        {currentPage === 'ai_chat' && <AIChatScreen />}
        {currentPage === 'goals' && <GoalsWithTabsScreen />}
        {currentPage === 'settings' && <SettingsScreen />}
        {currentPage === 'notifications' && <NotificationsScreen />}
        {currentPage === 'subscription' && <SubscriptionScreen />}
        {currentPage === 'plans' && <PlansScreen />}
        {currentPage === 'security' && <SecurityScreen />}
        {currentPage === 'privacy' && <PrivacyPolicyScreen />}
        {currentPage === 'terms' && <TermsOfServiceScreen />}
        {currentPage === 'request-password-reset' && <RequestPasswordResetScreen />}
        {currentPage === 'reset-password' && <ResetPasswordScreen />}
        {currentPage === 'accounts' && <AccountsScreen />}
      </div>
      {currentPage !== 'subscription' && currentPage !== 'plans' && currentPage !== 'security' && currentPage !== 'privacy' && currentPage !== 'terms' && currentPage !== 'request-password-reset' && currentPage !== 'reset-password' && currentPage !== 'accounts' && <BottomNav />}

      {/* Beta Banner Modal - Centered */}
      {showDevBanner && (
        <div
          className="modal-overlay modal-overlay--center"
          onClick={() => setShowDevBanner(false)}
        >
          <div
            className="beta-banner"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="beta-banner__close"
              onClick={() => setShowDevBanner(false)}
            >
              ×
            </button>

            {/* Content */}
            <div className="beta-banner__content">
              <h2 className="beta-banner__title">
                Бета-версия
              </h2>
              
              <p className="beta-banner__description">
                Приложение находится в активной разработке. Некоторые функции могут работать нестабильно.
              </p>

              <button
                className="beta-banner__button"
                onClick={() => setShowDevBanner(false)}
              >
                Понятно, начать работу
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>

      {/* Promo Code Modal */}
      {showPromoModal && promoCode && (
        <PromoCodeModal 
          promoCode={promoCode}
          onClose={() => setShowPromoModal(false)}
          onApply={() => {
            setShowPromoModal(false);
            setCurrentPage('subscription');
            // Передаем промокод через sessionStorage, чтобы его можно было применить на странице подписки
            sessionStorage.setItem('pendingPromoCode', promoCode);
            logger.info(`Navigating to subscription with promo code: ${promoCode}`);
          }}
        />
      )}
      
      {/* Platform Debugger - показывается по Ctrl+Shift+P */}
      {(import.meta.env.DEV || localStorage.getItem('show_platform_debugger') === 'true') && (
        <PlatformDebugger />
      )}
    </div>
  );
}

export default App;

